<template>
  <v-container fluid>
    <v-tabs v-model="tab" background-color="primary" dark>
      <v-tab value="user">User</v-tab>
      <v-tab value="role">Role</v-tab>
    </v-tabs>

    <v-tabs-window v-model="tab">
      <!-- Roles Tab -->
      <v-tabs-window-item value="role">
        <v-card>
          <v-card-title>
            <v-icon class="me-2">mdi-shield-account</v-icon>
            Roles
          </v-card-title>
          <v-card-subtitle>Manage your roles here.</v-card-subtitle>
          <v-card-actions>
            <v-btn @click="openRoleDialog" color="primary">
              <v-icon left>mdi-plus</v-icon>
              Add Role
            </v-btn>
          </v-card-actions>
          <v-data-table
            :headers="roleHeaders"
            :items="roles"
            item-value="role_id"
            class="elevation-1"
          >
          <template v-slot:item.action="{ item }">
            <v-btn 
              @click="editRole(item)" 
              color="primary" 
              variant="flat" 
              size="small" 
              prepend-icon="mdi-pencil"
              class="mx-2"
            >
              Edit
            </v-btn>
            <v-btn 
              @click="confirmDeleteRole(item.role_id)" 
              color="error" 
              variant="flat" 
              size="small" 
              prepend-icon="mdi-delete"
              class="mx-2"
            >
              Delete
            </v-btn>
          </template>

          </v-data-table>
        </v-card>
      </v-tabs-window-item>

      <!-- Users Tab -->
      <v-tabs-window-item value="user">
        <v-card>
          <v-card-title>
            <v-icon class="me-2">mdi-account</v-icon>
            Users
          </v-card-title>
          <v-card-subtitle>Manage your users here.</v-card-subtitle>
          <v-card-actions>
            <v-btn @click="openUserDialog" color="primary">
              <v-icon left>mdi-plus</v-icon>
              Add User
            </v-btn>
          </v-card-actions>

          <!-- Role Filter Chips -->
          <v-chip-group v-model="selectedRole">
            <v-chip
              v-for="role in roles"
              :key="role.role_id"
              :value="role.role_id"
              @click="filterByRole(role.role_id)"
              :class="{'v-chip--active': selectedRole == role.role_id}"
            >
              {{ role.role }}
            </v-chip>
          </v-chip-group>

          <v-data-table
            :headers="userHeaders"
            :items="filteredUsers"
            item-value="user_id"
            class="elevation-1"
          >
            <template v-slot:item.role="{ item }">
              <span>{{ item.role.role }}</span> <!-- Display role name -->
            </template>
            <template v-slot:item.action="{ item }">
              <v-btn 
                @click="editUser(item)" 
                color="primary" 
                variant="flat" 
                size="small" 
                prepend-icon="mdi-pencil"
                class="mx-2"
              >
                Edit
              </v-btn>
              <v-btn 
                @click="confirmDeleteUser(item.user_id)" 
                color="error" 
                variant="flat" 
                size="small" 
                prepend-icon="mdi-delete"
                class="mx-2"
              >
                Delete
              </v-btn>
            </template>

          </v-data-table>
        </v-card>
      </v-tabs-window-item>
    </v-tabs-window>

    <!-- Role Dialog -->
    <v-dialog v-model="roleDialog" max-width="500px" persistent scrollable>
  <v-card>
    <v-toolbar>
      <v-toolbar-title><strong>{{ editMode ? 'Edit Role' : 'New Role' }}</strong></v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="roleDialog = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <v-form @submit.prevent="submitRole">
        <v-text-field
        variant="outlined"
          v-model="role.role"
          label="Role Name"
          required
        ></v-text-field>
      </v-form>
    </v-card-text>
    <v-card-actions class="justify-end">
      <div class="text-center">
        <v-btn
          prepend-icon="mdi-check-circle"
          type="submit"
          variant="flat"
          color="primary"
          @click="submitRole"
        >
          {{ editMode ? 'Save' : 'Add' }}
        </v-btn>
        <v-btn
          prepend-icon="mdi-close-circle"
          @click="roleDialog = false"
          variant="flat"
          color="secondary"
        >
          Cancel
        </v-btn>
      </div>
    </v-card-actions>
  </v-card>
</v-dialog>


    

    <!-- User Dialog -->
    <v-dialog v-model="userDialog" max-width="500px" persistent scrollable>
  <v-card>
    <v-toolbar>
      <v-toolbar-title><strong>{{ editMode ? 'Edit User' : 'New User' }}</strong></v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="userDialog = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <v-form @submit.prevent="submitUser">
        <v-text-field
        variant="outlined"
          v-model="user.first_name"
          label="First Name"
          required
        ></v-text-field>
        <v-text-field
        variant="outlined"
          v-model="user.last_name"
          label="Last Name"
          required
        ></v-text-field>
        <v-text-field
        variant="outlined"
          v-model="user.email"
          label="Email"
          required
        ></v-text-field>
        <v-text-field
        variant="outlined"
          v-model="user.phone_number"
          label="Phone Number"
          required
        ></v-text-field>
        <v-text-field
        variant="outlined"
          v-model="user.username"
          label="Username"
          required
        ></v-text-field>
        <v-text-field
        variant="outlined"
          v-model="user.password"
          label="Password"
          type="password"
        ></v-text-field>
        <v-select
        variant="outlined"
          v-model="user.role_id"
          :items="roles"
          item-value="role_id"
          item-title="role"
          label="Role"
          required
        ></v-select>
      </v-form>
    </v-card-text>
    <v-card-actions class="justify-end">
      <div class="text-center">
        <v-btn
          prepend-icon="mdi-check-circle"
          type="submit"
          variant="flat"
          color="primary"
          @click="submitUser"
        >
          {{ editMode ? 'Save' : 'Add' }}
        </v-btn>
        <v-btn
          prepend-icon="mdi-close-circle"
          @click="userDialog = false"
          variant="flat"
          color="secondary"
        >
          Cancel
        </v-btn>
      </div>
    </v-card-actions>
  </v-card>
</v-dialog>


    <!-- Delete Confirmation Dialog -->
<v-dialog v-model="deleteDialog" max-width="400px">
  <v-card>
    <v-card-title class="headline">Delete Confirmation</v-card-title>
    <v-card-text>Are you sure you want to delete this item?</v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn 
        color="primary" 
        variant="flat" 
        size="small" 
        prepend-icon="mdi-close" 
        @click="deleteDialog = false"
      >
        Cancel
      </v-btn>
      <v-btn 
        color="error" 
        variant="flat" 
        size="small" 
        prepend-icon="mdi-delete" 
        @click="confirmDelete"
      >
        Delete
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>

  </v-container>
</template>

<script>
export default {
  data() {
    return {
      tab: 'user',
      roles: [],
      users: [],
      selectedRole: null, // Single selection for role filter
      roleHeaders: [
        { title: 'Role Name', key: 'role' },
        { title: 'Actions', key: 'action', sortable: false }
      ],
      userHeaders: [
        { title: 'First Name', key: 'first_name' },
        { title: 'Last Name', key: 'last_name' },
        { title: 'Email', key: 'email' },
        { title: 'Phone Number', key: 'phone_number' },
        { title: 'Role', key: 'role_id', value: 'role.role' },
        { title: 'Actions', key: 'action', sortable: false }
      ],
      roleDialog: false,
      userDialog: false,
      deleteDialog: false,
      editMode: false,
      role: { role_id: null, role: '' },
      user: {
        user_id: null,
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        username: '',
        password: '',
        role_id: null
      },
      itemToDelete: null
    };
  },
  computed: {
    filteredUsers() {
      if (!this.selectedRole) {
        return this.users; // No filter applied
      }
      return this.users.filter(user => user.role_id == this.selectedRole);
    }
  },
  async created() {
    await this.fetchRoles();
    await this.fetchUsers();
  },
  methods: {
    async fetchRoles() {
      try {
        const response = await this.$axios.get('/role', {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          }
        });
        this.roles = response.data.data || []; // Default to an empty array if data is null
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    },
    async fetchUsers() {
      try {
        const response = await this.$axios.get('/user', {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          }
        });
        this.users = response.data.data || []; // Default to an empty array if data is null
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },
    openRoleDialog() {
      this.role = { role_id: null, role: '' };
      this.editMode = false;
      this.roleDialog = true;
    },
    editRole(item) {
      this.role = { ...item };
      this.editMode = true;
      this.roleDialog = true;
    },
    async submitRole() {
      try {
        if (this.editMode) {
          await this.$axios.post(`/role/${this.role.role_id}`, this.role, {
            headers: {
              Authorization: 'Bearer ' + localStorage.getItem('token')
            }
          });
        } else {
          await this.$axios.post('/role', this.role, {
            headers: {
              Authorization: 'Bearer ' + localStorage.getItem('token')
            }
          });
        }
        this.roleDialog = false;
        await this.fetchRoles();
      } catch (error) {
        console.error('Error submitting role:', error);
      }
    },
    confirmDeleteRole(roleId) {
      this.itemToDelete = roleId;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      try {
        await this.$axios.delete(`/role/${this.itemToDelete}`, {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          }
        });
        this.deleteDialog = false;
        await this.fetchRoles();
      } catch (error) {
        console.error('Error deleting role:', error);
      }
    },
    openUserDialog() {
      this.user = {
        user_id: null,
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        username: '',
        password: '',
        role_id: null
      };
      this.editMode = false;
      this.userDialog = true;
    },
    editUser(item) {
      this.user = { ...item };
      this.editMode = true;
      this.userDialog = true;
    },
    async submitUser() {
      try {
        if (this.editMode) {
          await this.$axios.post(`/user/${this.user.user_id}`, this.user, {
            headers: {
              Authorization: 'Bearer ' + localStorage.getItem('token')
            }
          });
        } else {
          await this.$axios.post('/user', this.user, {
            headers: {
              Authorization: 'Bearer ' + localStorage.getItem('token')
            }
          });
        }
        this.userDialog = false;
        await this.fetchUsers();
      } catch (error) {
        console.error('Error submitting user:', error);
      }
    },
    confirmDeleteUser(userId) {
      this.itemToDelete = userId;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      try {
        await this.$axios.delete(`/user/${this.itemToDelete}`, {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          }
        });
        this.deleteDialog = false;
        await this.fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    },
    filterByRole(roleId) {
      this.selectedRole = roleId == this.selectedRole ? null : roleId; // Toggle filter
    }
  }
};
</script>

<style scoped>
.v-chip--active {
  background-color: #1976d2 !important; /* Change to your active chip color */
  color: white !important; /* Change to your active chip text color */
}
</style>
